import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { KIND, SIZE } from "baseui/button";
import { BottomPanel } from "components/bottom-panel";
import { Button } from "components/button";
import { Cell } from "components/cell";
import { Content } from "components/content";
import { Filters } from "components/filters";
import { DataType, FormattedValue } from "components/formatted-value";
import { Grid } from "components/grid";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { PagingControls } from "components/pagination";
import { Table } from "components/table";
import SortingTableHeader, {
  SortDirection,
} from "components/table/sorting-table-header";
import { Tooltip } from "components/tooltip";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { usePaging } from "contexts/paging-context";
import { useProject } from "contexts/project-context";
import { BasicFilter, FiltersState } from "filters";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Row } from "react-table";
import { Eye, Plus } from "tabler-icons-react";
import { translateFiltersState } from "utils/filters";
import { PERMISSIONS } from "utils/permissions";
import { renderUserLabel } from "utils/render-user-label";

import { CustomFile } from "../files";
import { FILES_FILTERS } from "../files.filters";
import { FilesField } from "../files.form";
import { FILES_INDEX, FILES_UPDATE } from "../files.gql";

export default function FilesIndex(): React.ReactElement {
  const { pageSize, currentPage, setTotalCount } = usePaging();
  const [sortBy, setSortBy] = useState<FilesField | null>(FilesField.CreatedAt);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(
    SortDirection.DESC
  );
  const { enqueueSnackbar } = useSnackbar();
  const [css] = useStyletron();
  const {
    isFetching,
    setIsFetching,
    setIsLoading,
    isPartialFetching,
    setIsPartialFetching,
  } = useLoading();
  const history = useHistory();
  const { checkPermission } = useAuth();
  const [filters, setFilters] = useState<FiltersState>();

  const { activeProject } = useProject();

  const handleSorting = (column: FilesField) => {
    setSortBy(column);
    setSortDirection(
      sortDirection === null
        ? SortDirection.DESC
        : sortDirection === SortDirection.ASC
        ? SortDirection.DESC
        : SortDirection.ASC
    );
  };

  const { refetch, data, loading, error } = useQuery(FILES_INDEX, {
    variables: {
      pageSize,
      offset: (currentPage - 1) * pageSize,
      sorting: {
        field: sortBy,
        direction: sortDirection,
      },
      filter: {
        and: [
          {
            ...(activeProject && {
              projectId: {
                eq: activeProject?.id,
              },
            }),
          },
          {
            ...(filters && (translateFiltersState(filters) as BasicFilter[])),
          },
        ],
      },
    },
  });
  const files: { totalCount: number; nodes: CustomFile[] } = data?.files;

  useEffect(() => {
    if (data?.files) setTimeout(() => refetch(), 100);
    setIsFetching(true);
  }, []);

  useEffect(() => {
    refetch();
    setIsPartialFetching(true);
  }, [currentPage, pageSize]);

  useEffect(() => {
    refetch();
    setIsPartialFetching(true);
  }, [sortBy, sortDirection]);

  useEffect(() => {
    if (data?.files) setIsFetching(false);
    if (files?.totalCount >= 0) setTotalCount(files?.totalCount);
  }, [data]);

  useEffect(() => {
    if (error?.graphQLErrors)
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [error]);

  const [updateFile] = useMutation(FILES_UPDATE);

  const onSubmit = async (file: CustomFile): Promise<void> => {
    setIsLoading(true);

    try {
      await updateFile({
        variables: {
          fileUpdateInput: {
            id: file.id,
            isVisible: !file.isVisible,
          },
        },
      });
      enqueueSnackbar({
        message: `Plik "${file.name}" jest ${
          file.isVisible ? "niewidoczny" : "widoczny"
        }`,
        variant: "success",
      });
      refetch();
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(FilesField.Name)}
            sortDirection={sortBy === FilesField.Name ? sortDirection : null}
          >
            Nazwa
          </SortingTableHeader>
        ),
        accessor: FilesField.Name,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <FormattedValue dataType={DataType.Files} data={row.original.id}>
            {row.original.name}
          </FormattedValue>
        ),
        disableGlobalFilter: true,
      },
      {
        Header: "Projekt",
        accessor: FilesField.Project,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <FormattedValue
            dataType={DataType.Projects}
            data={row.original?.project?.id}
            $style={{ fontSize: "12px" }}
          >
            {row.original?.project?.name}
          </FormattedValue>
        ),
      },
      {
        Header: "Wersja językowa",
        accessor: FilesField.Language,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <FormattedValue
            dataType={DataType.Language}
            data={row.original?.project?.id}
            $style={{ fontSize: "12px" }}
          >
            {row.original?.language}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(FilesField.Name)}
            sortDirection={sortBy === FilesField.Name ? sortDirection : null}
          >
            Nazwa pliku
          </SortingTableHeader>
        ),
        accessor: FilesField.Filename,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <FormattedValue data={row.original.id}>
            {row.original.filename}
          </FormattedValue>
        ),
      },
      {
        Header: "Rozszerzenie",
        id: "format",
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <FormattedValue dataType={DataType.FileFormat}>
            {row.original?.filename}
          </FormattedValue>
        ),
      },
      {
        Header: "Rozmiar",
        accessor: FilesField.Size,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <FormattedValue dataType={DataType.FileSize}>
            {row.original?.size}
          </FormattedValue>
        ),
      },
      {
        Header: (
          <SortingTableHeader
            onClick={() => handleSorting(FilesField.IsVisible)}
            sortDirection={
              sortBy === FilesField.IsVisible ? sortDirection : null
            }
          >
            Widoczność
          </SortingTableHeader>
        ),
        accessor: FilesField.IsVisible,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <Tooltip
            content={`Ustaw jako ${
              row.original.isVisible ? "niewidoczny" : "widoczny"
            }`}
            placement="right"
          >
            <span
              onClick={() => onSubmit(row.original)}
              className={css({
                cursor: "pointer",
              })}
            >
              <FormattedValue dataType={DataType.VisibilityBoolean}>
                {row.original.isVisible}
              </FormattedValue>
            </span>
          </Tooltip>
        ),
      },
      {
        Header: (
          <Block display="flex" justifyContent="flex-end">
            <SortingTableHeader
              onClick={() => handleSorting(FilesField.CreatedAt)}
              sortDirection={
                sortBy === FilesField.CreatedAt ? sortDirection : null
              }
            >
              Utworzenie
            </SortingTableHeader>
          </Block>
        ),
        accessor: FilesField.CreatedAt,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <Block display="flex" justifyContent="flex-end">
            <FormattedValue dataType={DataType.Date}>
              {row.original.createdAt}
            </FormattedValue>
          </Block>
        ),
      },
      {
        Header: "Utworzono przez",
        accessor: FilesField.CreatedBy,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <FormattedValue
            dataType={DataType.Users}
            data={row.original?.createdBy?.id}
          >
            {row.original?.createdBy
              ? renderUserLabel(row.original?.createdBy)
              : undefined}
          </FormattedValue>
        ),
      },
      {
        Header: "Zaktualizowano przez",
        accessor: FilesField.UpdatedBy,
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <FormattedValue
            dataType={DataType.Users}
            data={row.original?.updatedBy?.id}
          >
            {row.original?.updatedBy
              ? renderUserLabel(row.original?.updatedBy)
              : undefined}
          </FormattedValue>
        ),
      },

      {
        id: "actions",
        Cell: ({ row }: { row: Row<CustomFile> }) => (
          <div
            className={css({
              display: "flex",
              justifyContent: "flex-end",
            })}
          >
            <Button
              kind={KIND.secondary}
              size={SIZE.mini}
              $style={{ marginLeft: "6px" }}
              onClick={() => history.push(`/files/${row.original.id}`)}
              startEnhancer={<Eye size={14} />}
            />
          </div>
        ),
      },
    ],
    [sortBy, sortDirection]
  );

  if (!checkPermission(PERMISSIONS.file.read)) return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title="Pliki"
        recordsNum={files?.totalCount}
        labels={["Lista"]}
        buttons={[
          {
            label: "Dodaj plik",
            kind: KIND.primary,
            startEnhancer: <Plus size={18} />,
            permission: checkPermission(PERMISSIONS.file.create),
            onClick: () => history.push("/files/create"),
          },
        ]}
      />
      <Filters
        filters={FILES_FILTERS.filter((filter: BasicFilter) =>
          activeProject ? filter.id !== FilesField.Project : filter
        )}
        state={filters}
        setState={setFilters}
      />
      <Content filtersOffset>
        <Grid>
          <Cell span={12}>
            <Table<CustomFile>
              columns={columns}
              data={files?.nodes}
              isLoading={isFetching || isPartialFetching || loading}
              stickLastColumn
            />
          </Cell>
          <Cell span={12}>
            <BottomPanel>
              <PagingControls />
            </BottomPanel>
          </Cell>
        </Grid>
      </Content>
    </article>
  );
}
